import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';
import { BehaviorSubject, Subscription, combineLatest, filter } from 'rxjs';
import moment from 'moment-timezone';

import { MediaSubscriptionMediaTypeEnum, mediaSubTypeToMediaTypeMap } from '../../../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { ChartMetadataModel } from '../../../../../Common/Charts/models/chart-metadata.model';
import { ChartModel } from '../../../../../Common/Charts/models/chart.model';
import { DataPointModel } from '../../../../../Common/Charts/models/data-point.model';
import { HorizontalStackedBarChartModel } from '../../../../../Common/Charts/models/stackedBarChart/horizontal-stacked-bar-chart.model';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { ThemeAnalyticsChartSeriesService } from '../theme-analytics-chart-series.service';
import { ThemeAnalyticsChartTypeEnum } from '../../../enums/theme-analytics-chart-type.enum';
import { SocialChartDetailsDialogService } from '../../../services/social-chart-details-dialog.service';
import { CardChartComponentHighlightColorEnum } from '../../../../../Common/Charts/components/CardChart/card-chart.component';
import { SocialSeriesService } from '../../../services/social-series.service';
import { ReportSeriesFacetGeneratorEnum } from '../../../../../Common/Reports/models/report-series-facet-generator.enum';
import { SocialSeriesNeoFacetKeysEnum } from '../../../enums/social-series-neo-facet-keys.enum';
import { NeoFacetRequestApiModel } from '../../../../../api/mentions/model/neo-facet-request-api.model';
import { NeoFacetTermFieldApiEnum } from '../../../../../api/mentions/enum/neo-facet-term-field-api.enum';
import { MentionSearchFacetTypesEnum } from '../../../../../api/mentions/enum/mention-search-facet-types.enum';
import { chartColors } from '../../../../../Common/Charts/enums/chart-colors.enum';
import { Mention } from '../../../../../Common/Mentions/models/mention';
import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '../../../../../api/shared/enums/sort-order.enum';
import { MentionSearchModel } from '../../../../../Monitor/Shared/models/mention-search.model';
import { SentimentModel } from '../../../../../Monitor/Shared/models/sentiment.model';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { NeoFacetDateFieldApiEnum } from '../../../../../api/mentions/model/neo-facet-date-field-api.enum';
import { MultiSeriesPieChartModel } from '../../../../../Common/Charts/models/pieChart/multiSeriesPieChart/multi-series-pie-chart.model';
import { BucketResponseModel } from '../../../../../api/mentions/model/bucket-response.model';
import { MediaTypeModel } from '../../../../../Monitor/Shared/models/media-type.model';
import { MentionSearchCriteriaModel } from '../../../../../Monitor/Shared/models/mention-search-criteria.model';
import { socialSentimentToSearchCriteriaQuerySentimentsEnumMap } from '../../../enums/social-term-maps';
import { getSentimentMapper } from '../ThemeAnalyticsSummary/theme-analytics-summary.component';
import { DateQueryModel } from '../../../../../Common/Forms/models/date-query.model';
import { SocialSeriesMetricsEnum } from '../../../enums/social-series-metrics.enum';
import { NetworkTypeEnum } from '../../../../Core/enums/network-type.enum';
import { TopTermsDataMappers } from '@Social/Shared/components/TopTerms/top-terms-data-mappers';
import { ChartLoadingOptionsEnum } from '../../../../../Common/Charts/enums/chart-loading-options.enum';
import { ChartDisplaySettingsModel } from '../../../../../Common/Charts/models/chart-display-settings.model';
import { AgprComponentStyleEnum } from '../../../../../Common/Basic/enums/social-analysis-types.enum';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { AgentService } from '../../../services/agent.service';
import { MediaSubscription } from '../../../../../Monitor/Shared/models/media-subscription.model';
import { SocialRoutingService } from '../../../../Core/services/social-routing.service';
import { SocialArchiveSearchService } from '../../../services/social-archive-search.service';
import { SocialChartExportModel, SocialChartExportService } from '../../../services/social-chart-export.service';
import { ChartExportEventModel } from '../../../../../Common/Charts/models/chart-export-event.model';
import { userAppPreferencesDefault } from '../../../../../Core/Users/data/user-app-preferences-default';
import { socialNetworkTypeColorMap } from '../../../../../Core/Users/data/social-network-type-color-map';
import { ThemeAnalyticsChartOptionsService } from '../theme-analytics-chart-options.service';
import { ReportSeriesModel } from '../../../../../Common/Reports/models/report-series.model';
import { Am5DateValueXYChart } from '../../../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { Am5Chart } from '../../../../../Common/Charts/models/am5-chart.model';
import { Am5ChartConfigurationService } from '../../../../../Common/Reports/services/am5-chart-configuration.service';
import { Am5ChartTooltipService } from '../../../../../Common/Charts/services/am5-chart-tooltip.service';
import { ChartDisplayElementModel } from '../../../../../Common/Reports/models/chart-display-element.model';
import { ChartStylesModel } from '../../../../../Common/Charts/models/chart-styles.model';
import { reportBlockTypesMap } from '../../../../../Common/Reports/models/reports-block-types.model';
import { BlockModel } from '../../../../../Common/PageBuilder/models/block.model';

import { MediaTypeEnum } from '../../../../../Common/Basic/enums/media-type.enum';
import { ReportsDataMappingKeysEnum } from '../../../../../Common/Reports/models/reports-block-mapping-configuration.model';
import { safeDivide } from '../../../../../Core/utils';
import { ReportsChartSeriesConverterService } from '../../../../../Common/Reports/services/reports-chart-series-converter.service';
import { ReportBlockTypeEnum } from '../../../../../Common/Reports/enums/report-block-type.enum';
import { ChartSpecificTypesEnum } from '../../../../../Common/Reports/enums/chart-specific-types.enum';
import { stackedBarColorsConfig } from '../../../../../Common/Charts/config/stacked-bar-colors.config';
import { StackTypesEnum } from '../../../../../Common/Charts/models/barChart/category-value-x-y-chart-options.model';
import { ChartTimeIntervalsEnum } from '../../../../../Common/Reports/enums/chart-time-intervals.enum';
import { getTimeScale } from '../../../../../Common/Forms/helpers/time.helper';
import { StackedChartTypesEnum } from '../../../../../Common/Reports/enums/stacked-chart-types.enum';
import { networkTypeToMediaTypeMap } from '../../../models/network-type-to-media-type-model.map';
import { Am5ChartSettingsService } from '../../../../../Common/Reports/services/am5-chart-settings.service';

@Component({
    selector: 'agpr-theme-sentiment-analytics',
    templateUrl: './theme-sentiment-analytics.component.html',
    styleUrls: ['./theme-sentiment-analytics.component.scss'],
    providers: [
        Am5ChartTooltipService
    ],
    standalone: false
})
export class ThemeSentimentAnalyticsComponent implements OnInit, OnDestroy {
    readonly cardChartComponentHighlightColorEnum = CardChartComponentHighlightColorEnum;
    readonly socialMetrics = SocialSeriesMetricsEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly mentionSearchFacetTypesEnum = MentionSearchFacetTypesEnum;
    readonly chartColors = chartColors;
    readonly stackTypes = StackTypesEnum;
    readonly supportedExportFormats = [ 'png', 'jpg', 'svg', 'json', 'csv', 'print' ];
    readonly topTermsPositiveConfig = {
        mainTitle: {
            mode: 'single',
            title: 'Social.intelligence.charts.topTerms.heading.positive',
        },
        subTitle: {
            mode: 'dynamic',
            title: 'Social.intelligence.charts.topTerms.heading.positive'
        }
    };
    readonly topTermsNegativeConfig = {
        mainTitle: {
            mode: 'single',
            title: 'Social.intelligence.charts.topTerms.heading.negative',
        },
        subTitle: {
            mode: 'dynamic',
            title: 'Social.intelligence.charts.topTerms.heading.negative'
        }
    };
    readonly sentimentsToChartColorsMap = new Map<SearchCriteriaQuerySentimentsEnum, chartColors>([
        [SearchCriteriaQuerySentimentsEnum.positive, chartColors.agprPositive],
        [SearchCriteriaQuerySentimentsEnum.neutral, chartColors.agprNeutral],
        [SearchCriteriaQuerySentimentsEnum.negative, chartColors.agprNegative],
        [SearchCriteriaQuerySentimentsEnum.notAvailable, chartColors.agprNotAvailable]
    ]);
    readonly themeAnalyticsChartTypeEnum = ThemeAnalyticsChartTypeEnum;
    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    // Card charts at the top of the page
    public positiveSeries: ReportSeriesModel;
    public previousPeriodPositiveSeries: ReportSeriesModel;

    public negativeSeries: ReportSeriesModel;
    public previousPeriodNegativeSeries: ReportSeriesModel;

    // Sentiment summary
    private sentimentSummarySeries: ReportSeriesModel;
    private previousPeriodSentimentSummarySeries: ReportSeriesModel;
    public sentimentSummaryChart: MultiSeriesPieChartModel;
    public sentimentSummaryChartLoading: boolean;
    private sentimentSummaryChartLoadingSubscription: Subscription;
    selectedSentimentTrendTopicChartTabIndex: number = 0;
    selectedSentimentTrendNetworkChartTabIndex: number = 0;

    // Sentiment Comparison
    // By Social Network
    private sentimentBySocialNetworkComparisonSeries: ReportSeriesModel;
    public sentimentBySocialNetworkComparisonChart: HorizontalStackedBarChartModel;
    // By Social Topic
    private sentimentBySocialTopicComparisonSeries: ReportSeriesModel;
    public sentimentBySocialTopicComparisonChart: HorizontalStackedBarChartModel;

    // sentiment trend chart
    // Social Network
    private socialNetworkTrendChartSeries: ReportSeriesModel;
    public socialNetworkTrendChart: Am5DateValueXYChart;
    public socialNetworkTrendSeries: ReportSeriesModel[];
    public socialNetworkTrendChartNoData: boolean = false;

    public showSentimentBySocialNetworkTrendChart: boolean = false;
    public selectedSocialMedia: MediaSubscriptionMediaTypeEnum;
    // Social Topic
    private socialTopicTrendChartSeries: ReportSeriesModel;
    public socialTopicTrendChart: Am5DateValueXYChart;
    public socialTopicTrendSeries: ReportSeriesModel[];
    public socialTopicTrendChartNoData: boolean = false;

    public showSentimentBySocialTopicTrendChart: boolean = false;
    public selectedSocialTopicName: string;

    // Positive Top Posts
    public positiveTopPostsSeries: ReportSeriesModel;
    public positiveTopPostsSortField: SearchCriteriaSortFieldEnum;

    // Negative Top Posts
    public negativeTopPostsSeries: ReportSeriesModel;
    public negativeTopPostsSortField: SearchCriteriaSortFieldEnum;

    // Positive Top Terms
    public positiveTopTermsSeries: ReportSeriesModel;
    public positiveTopTermsSortField: SearchCriteriaSortFieldEnum;

    // Negative Top Terms
    public negativeTopTermsSeries: ReportSeriesModel;
    public negativeTopTermsSortField: SearchCriteriaSortFieldEnum;

    idToChartModelMap: Map<string, ChartModel | Am5Chart>;
    public enabledNetworkTypes: NetworkTypeEnum[] = [
        NetworkTypeEnum.all,
        NetworkTypeEnum.twitter,
        NetworkTypeEnum.facebook,
        NetworkTypeEnum.instagram,
        NetworkTypeEnum.youtube,
        NetworkTypeEnum.reddit,
        NetworkTypeEnum.linkedin
    ];

    subscriptions: Subscription[] = [];
    chartLoadingOptionsEnum = ChartLoadingOptionsEnum;
    isArchiveSearch: boolean = false;
    enableEngagementInsights: boolean = true;
    hideSeries: ReportSeriesModel[];
    disabledSeries: ReportSeriesModel[];
    hasTooManyResources: boolean = false; // indicate if there are more than 8 resources in Social Topic Comparison Chart

    private socialTopics: MediaSubscription[];
    private agentIds: number[] = [];
    @Input() id: string;
    @ViewChild('sentimentSummaryChartLegend', {read: ElementRef, static: true}) sentimentSummaryChartLegend: ElementRef;

    noContentLabelText: string;

    constructor(
        public localizationService: LocalizationService,
        public themeSentimentAnalyticsChartSeriesService: ThemeAnalyticsChartSeriesService,
        public themeAnalyticsChartOptionsService: ThemeAnalyticsChartOptionsService,
        public socialSeriesService: SocialSeriesService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public agentService: AgentService,
        private socialRoutingService: SocialRoutingService,
        private socialArchiveSearchService: SocialArchiveSearchService,
        public chartExportService: SocialChartExportService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public am5ChartConfigurationService: Am5ChartConfigurationService,
        public am5ChartSettingsService: Am5ChartSettingsService,
        public am5ChartTooltipService: Am5ChartTooltipService,
    ) {
        this.noContentLabelText = this.localizationService.transform('Social.intelligence.wordCloudChart.noContent');
    }

    @HostListener('click', ['$event.target'])
    onClick(event){
        if (event?.classList.contains('viewMentionLink') && event?.classList.length > 1) {
            const chartClass = event.classList[1];
            let chartModel = this.idToChartModelMap.get(chartClass);

            chartModel = this.prepareChartsForDrillDownPage(chartModel);
            this.socialChartDetailsDialogService.openDrillDownPage(
                event.href,
                chartModel?.clone ? chartModel.clone() : chartModel
            );
            return false;
        }
    }

    ngOnInit(): void {
        this.subscriptions.push(
            combineLatest(
                [
                    this.agentService.all$.pipe(filter(x => !!x)),
                    this.socialSeriesService.agentIds$.pipe(filter(x => !!x)),
                    this.socialRoutingService.isArchiveSearch$,
                    this.socialSeriesService.availableNetworks$
                ]
            ).subscribe(
                ([socialTopics, agentIds, isArchiveSearch, availableNetworks]) => {
                    this.isArchiveSearch = isArchiveSearch;
                    this.deregisterSeries();
                    this.socialTopics = socialTopics;
                    this.agentIds = agentIds;
                    this.enabledNetworkTypes = availableNetworks;
                    this.initializeAll();
                }
            ),
            this.socialSeriesService.availableNetworks$.subscribe(availableNetworks => this.enabledNetworkTypes = availableNetworks),
            this.socialArchiveSearchService.enableEngagementInsights$.subscribe(
                enableEngagementInsights => this.enableEngagementInsights = enableEngagementInsights
            )
        );
    }

    ngOnDestroy() {
        this.deregisterSeries();
        this.subscriptions.forEach(sub => sub.unsubscribe());
        if (this.sentimentSummaryChartLoadingSubscription) {
            this.sentimentSummaryChartLoadingSubscription.unsubscribe();
        }
    }

    private initializeAll(): void {
        this.initializeCardCharts();
        this.initializeSentimentSummaryChart();
        this.initializeSentimentBySocialMediaComparisonChart();
        this.initializeSentimentBySocialTopicComparisonChart();
        this.initializeSentimentBySocialMediaTrendChart();
        this.initializeSentimentBySocialTopicTrendChart();
        this.initializeTopPosts();
        this.initializeTopTerms();

        this.idToChartModelMap = new Map<string, ChartModel | Am5Chart>([
            // the string here should match the id in the tooltip link id in tooltipHTML
            ['sentimentBySocialMediaComparisonChart', this.sentimentBySocialNetworkComparisonChart],
            ['sentimentBySocialTopicComparisonChart', this.sentimentBySocialTopicComparisonChart],
            ['stackedTrendChartSocialNetwork', this.socialNetworkTrendChart],
            ['stackedTrendChartSocialTopic', this.socialTopicTrendChart],
            ['sentimentSummaryChart', this.sentimentSummaryChart]
        ]);

        if (this.isArchiveSearch) {
            this.hideSeries = [
                // Card series
                this.previousPeriodPositiveSeries,
                this.previousPeriodNegativeSeries,
                // Sentiment Summary
                this.previousPeriodSentimentSummarySeries,
                // Sentiment Comparison
                this.sentimentBySocialTopicComparisonSeries,
                // sentiment trend Chart
                this.socialTopicTrendChartSeries,
            ];
        } else {
            this.hideSeries = [];
        }

        this.socialSeriesService.fulfillSeries(
            [
                // Card series
                this.positiveSeries,
                this.previousPeriodPositiveSeries,
                this.negativeSeries,
                this.previousPeriodNegativeSeries,
                // Sentiment Summary
                this.sentimentSummarySeries,
                this.previousPeriodSentimentSummarySeries,
                // Sentiment Comparison
                this.sentimentBySocialNetworkComparisonSeries,
                this.sentimentBySocialTopicComparisonSeries,
                // sentiment trend Chart
                this.socialNetworkTrendChartSeries,
                this.socialTopicTrendChartSeries,
                // TopTrends
                this.positiveTopPostsSeries,
                this.negativeTopPostsSeries,
                // Top Terms
                this.positiveTopTermsSeries,
                this.negativeTopTermsSeries,
            ].filter(x => !this.hideSeries.find(series => x === series))
        );

        this.hideSeries.forEach(series => series.dataProvider.next([]));
    }

    private deregisterSeries(): void {
        this.socialSeriesService.deregisterSeries(
            [
                // Card series
                this.positiveSeries,
                this.previousPeriodPositiveSeries,
                this.negativeSeries,
                this.previousPeriodNegativeSeries,
                // Sentiment Summary
                this.sentimentSummarySeries,
                this.previousPeriodSentimentSummarySeries,
                // Sentiment Comparison
                this.sentimentBySocialNetworkComparisonSeries,
                this.sentimentBySocialTopicComparisonSeries,
                // sentiment trend Chart
                this.socialNetworkTrendChartSeries,
                this.socialTopicTrendChartSeries,
                // TopTrends
                this.positiveTopPostsSeries,
                this.negativeTopPostsSeries,
                // Top Terms
                this.positiveTopTermsSeries,
                this.negativeTopTermsSeries,
            ].filter(x => !this.hideSeries?.find(series => x === series))
        );
    }

    sentimentTrendTopicChartStackTypeChanged(index: number): void {
        this.selectedSentimentTrendTopicChartTabIndex = index;
        this.socialTopicTrendChart = this.buildSocialSentimentTrendChart(
            this.socialTopicTrendChartSeries, index, 'stackedTrendChartSocialTopic'
        );
        this.idToChartModelMap?.set('stackedTrendChartSocialTopic', this.socialTopicTrendChart);
    }

    sentimentTrendNetworkChartStackTypeChanged(index: number): void {
        this.selectedSentimentTrendNetworkChartTabIndex = index;
        this.socialNetworkTrendChart = this.buildSocialSentimentTrendChart(
            this.socialNetworkTrendChartSeries, index, 'stackedTrendChartSocialNetwork'
        );
        this.idToChartModelMap?.set('stackedTrendChartSocialNetwork', this.socialNetworkTrendChart);
    }

    private buildSocialSentimentTrendChart(
        series: ReportSeriesModel,
        tabIndex: number,
        id: string
    ): Am5DateValueXYChart {
        const stackedChartTypes = tabIndex === 0
            ? StackedChartTypesEnum.PERCENTAGE
            : StackedChartTypesEnum.VOLUME;

        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = series.styles.colorMap;
        chartStyles.chartDisplaySettings = new ChartDisplaySettingsModel({
            title: false,
            dateRange: false,
            sourceLink: false,
            previousPeriod: false,
            multiSource: false,
            trendIndicators: false
        });
        const chartDisplayElement = new ChartDisplayElementModel('', [series], chartStyles);
        chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
        chartDisplayElement.stackedChartTypes = stackedChartTypes;
        chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;

        const sentimentTrendBlock = new BlockModel(
            null,
            reportBlockTypesMap.get(ReportBlockTypeEnum.SENTIMENT_TREND),
            chartDisplayElement
        );
        const chartSeries = this.reportsChartSeriesConverterService.getChartSeries([series], sentimentTrendBlock);
        const chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(
            id, ReportBlockTypeEnum.SENTIMENT_TREND, chartDisplayElement
        );
        chartConfiguration.header.hasHeader = false;
        chartConfiguration.noContent.isMessageStyle = true;
        const chartSettings = this.am5ChartSettingsService.getChartSettings(
            ReportBlockTypeEnum.SENTIMENT_TREND, chartDisplayElement
        );
        this.am5ChartTooltipService.setDoLink(true);
        chartSeries.forEach((x, i) => {
            x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip(
                chartSeries, chartConfiguration, chartSettings, i, false
            );
        });
        return new Am5DateValueXYChart(chartSeries, chartConfiguration, chartSettings);
    }

    openSocialTrendDrilldown(event: any, chartModel: Am5Chart): void {
        const url = event?.data;
        if (url && chartModel) {
            this.socialChartDetailsDialogService.openDrillDownPage(url, chartModel.clone());
        }
    }

    updateSocialNetworkTrendChartNoData(noData: boolean): void {
        this.socialNetworkTrendChartNoData = noData;
    }

    updateSocialTopicTrendChartNoData(noData: boolean): void {
        this.socialTopicTrendChartNoData = noData;
    }

    private initializeCardCharts(): void {
        // positive series setup
        this.positiveSeries = new ReportSeriesModel();
        this.positiveSeries.label = 'Social.intelligence.themeAnalytics.sentiment.positive';
        this.positiveSeries.mentions.facetGenerator = () => {
            return {
                facet:  [MentionSearchFacetTypesEnum.sentiment],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
            /*
            @TODO: neoFacet only loads top 10 values, sometimes need up to 20 values to do it properly [-1.0, -0.9... 0.9, 1.0]
            return {
                facet: sentimentNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
            */
        };
        this.positiveSeries.mentions.dataMapper = (response) => {
            const filterOverrides = new MentionSearchCriteriaModel();
            filterOverrides.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
            let viewMentionUrl: string = '';
            try {
                viewMentionUrl = this.positiveSeries.getSocialSeriesUrl(filterOverrides);
            } catch {}

            const sentimentFacet = response.facetResults.find(facetResult => facetResult.field === MentionSearchFacetTypesEnum.sentiment);
            const count = sentimentFacet?.values.reduce((previousValue, value) => {
                return +value.value >= 0.3 ? previousValue + value.count : previousValue;
            }, 0);
            return [{
                count: count,
                viewMentionUrl: viewMentionUrl
            }];

            /**
             * @TODO: Neo facet approach
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.sentimentFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => {
                return +bucket.termValue >= 0.3 ? previousValue + bucket.count : previousValue;
            }, 0);
            return [{
                count: count
            }];
             */
        };

        // previous positive series setup
        this.previousPeriodPositiveSeries = new ReportSeriesModel();
        this.previousPeriodPositiveSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodPositiveSeries.mentions.facetGenerator = () => {
            return {
                facet:  [MentionSearchFacetTypesEnum.sentiment],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };
        this.previousPeriodPositiveSeries.mentions.dataMapper = (response) => {

            const filterOverrides = new MentionSearchCriteriaModel();
            filterOverrides.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
            let viewMentionUrl: string = '';
            try {
                viewMentionUrl = this.previousPeriodPositiveSeries.getSocialSeriesUrl(filterOverrides);
            } catch {}

            const sentimentFacet = response.facetResults.find(facetResult => facetResult.field === MentionSearchFacetTypesEnum.sentiment);
            const count = sentimentFacet?.values.reduce((previousValue, value) => {
                return +value.value >= 0.3 ? previousValue + value.count : previousValue;
            }, 0);
            return [{
                count: count,
                viewMentionUrl: viewMentionUrl
            }];
        };

        // negative series setup
        this.negativeSeries = new ReportSeriesModel();
        this.negativeSeries.label = 'Social.intelligence.themeAnalytics.sentiment.negative';
        this.negativeSeries.mentions.facetGenerator = () => {
            return {
                facet:  [MentionSearchFacetTypesEnum.sentiment],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };
        this.negativeSeries.mentions.dataMapper = (response) => {
            const filterOverrides = new MentionSearchCriteriaModel();
            filterOverrides.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
            let viewMentionUrl: string = '';
            try {
                viewMentionUrl = this.negativeSeries.getSocialSeriesUrl(filterOverrides);
            } catch {}

            const sentimentFacet = response.facetResults.find(facetResult => facetResult.field === MentionSearchFacetTypesEnum.sentiment);
            const count = sentimentFacet?.values.reduce((previousValue, value) => {
                return +value.value <= -0.3 ? previousValue + value.count : previousValue;
            }, 0);
            return [{
                count: count,
                viewMentionUrl: viewMentionUrl
            }];
        };

        // previous negative series setup
        this.previousPeriodNegativeSeries = new ReportSeriesModel();
        this.previousPeriodNegativeSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodNegativeSeries.mentions.facetGenerator = () => {
            return {
                facet:  [MentionSearchFacetTypesEnum.sentiment],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };
        this.previousPeriodNegativeSeries.mentions.dataMapper = (response) => {
            const filterOverrides = new MentionSearchCriteriaModel();
            filterOverrides.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
            let viewMentionUrl: string = '';
            try {
                viewMentionUrl = this.previousPeriodNegativeSeries.getSocialSeriesUrl(filterOverrides);
            } catch {}

            const sentimentFacet = response.facetResults.find(facetResult => facetResult.field === MentionSearchFacetTypesEnum.sentiment);
            const count = sentimentFacet?.values.reduce((previousValue, value) => {
                return +value.value <= -0.3 ? previousValue + value.count : previousValue;
            }, 0);
            return [{
                count: count,
                viewMentionUrl: viewMentionUrl
            }];
        };
    }

    public initializeSentimentSummaryChart() {
        this.sentimentSummarySeries = new ReportSeriesModel();
        this.sentimentSummarySeries.mentions.facetGenerator = () => {
            return {
                facet: [MentionSearchFacetTypesEnum.sentiment],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };
        this.sentimentSummarySeries.mentions.dataMapper = getSentimentMapper(this.sentimentSummarySeries, this.localizationService);

        this.previousPeriodSentimentSummarySeries = new ReportSeriesModel();
        this.previousPeriodSentimentSummarySeries.label = this.localizationService.transform('Social.intelligence.themeAnalytics.previousPeriod');
        this.previousPeriodSentimentSummarySeries.mentions.isPreviousPeriod = true;
        this.previousPeriodSentimentSummarySeries.mentions.facetGenerator = () => {
            return {
                facet: [MentionSearchFacetTypesEnum.sentiment],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };
        this.previousPeriodSentimentSummarySeries.mentions.dataMapper = getSentimentMapper(
            this.previousPeriodSentimentSummarySeries,
            this.localizationService
        );

        const mockOptions = this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SENTIMENT_SUMMARY);
        mockOptions.legendContainerElement = this.sentimentSummaryChartLegend.nativeElement;

        const mockMetadata = {
            titleText: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.sentimentSummary'),
            dateRange: {
                type: 'RELATIVE',
                relativeStartOffsetH: -24,
                relativeEndOffsetH: 0
            },
            labelDisplayOptions: [
                'percent'
            ],
            noDataFromDateRangeLabel: this.localizationService.transform('Social.intelligence.themeAnalytics.chart.noDataFromDateRange'),
            enableDataDisplaySettings: false,
            titleHeadingLevel: 4
        } as ChartMetadataModel;

        this.sentimentSummaryChartLoading = true;
        if (this.sentimentSummaryChartLoadingSubscription) {
            this.sentimentSummaryChartLoadingSubscription.unsubscribe();
        }
        this.sentimentSummaryChartLoadingSubscription = combineLatest([
            this.sentimentSummarySeries.dataProvider,
            this.previousPeriodSentimentSummarySeries.dataProvider
        ]).subscribe(
            ([curr, prev]) => {
                this.sentimentSummaryChartLoading = true;
                if (curr && prev) {
                    requestAnimationFrame(() => {
                        this.sentimentSummaryChartLoading = false;
                    });
                }
            }
        );

        this.sentimentSummaryChart = new MultiSeriesPieChartModel(
            [this.previousPeriodSentimentSummarySeries, this.sentimentSummarySeries],
            mockOptions,
            mockMetadata
        );
    }

    private initializeSentimentBySocialMediaComparisonChart(): void {
        this.sentimentBySocialNetworkComparisonSeries = new ReportSeriesModel();
        this.sentimentBySocialNetworkComparisonSeries.mentions.facetGenerator = () => {
            return {
                facet: sentimentNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.sentimentBySocialNetworkComparisonSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.sentimentFacetGroup);
            const returnArray: DataPointModel[] = [];
            if (!neoFacet?.buckets) {
                return returnArray;
            }
            const buckets = this.fillBucketWithAllMediaTypes(neoFacet.buckets);
            buckets.forEach(
                bucket => {
                    const values = {
                        positive: 0,
                        neutral: 0,
                        negative: 0,
                        // needs to follow this convention so key.toUpperCase maps to the right thing...
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        not_Available: 0
                    };
                    (bucket.buckets[SocialSeriesNeoFacetKeysEnum.sentimentByMediaTypeFacetGroup] as any).forEach((mediaTypeBucket) => {
                        if (+mediaTypeBucket.termValue <= -0.3) {
                            values.negative += mediaTypeBucket.count;
                        } else if (+mediaTypeBucket.termValue >= 0.3) {
                            values.positive += mediaTypeBucket.count;
                        } else {
                            values.neutral += mediaTypeBucket.count;
                        }
                    });

                    values.not_Available = bucket.count - values.positive - values.negative - values.neutral;

                    returnArray.push(...Object.keys(values).map(
                        (key, value) => {
                            const filterOverrides = new MentionSearchCriteriaModel();

                            // map 'TWEET' for backwards compatibility
                            if (bucket.termValue === 'TWEET') {
                                bucket.termValue = MediaTypeEnum.TWITTER;
                            }

                            filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];

                            // Necessary for socialSentimentToSearchCriteriaQuerySentimentsEnumMap
                            let k = key;
                            if (key === 'not_Available') {
                                k = 'notAvailable';
                            }
                            filterOverrides.query.sentiments = [new SentimentModel(socialSentimentToSearchCriteriaQuerySentimentsEnumMap.get(k))];

                            let viewMentionUrl: string = '';
                            try {
                                viewMentionUrl = this.sentimentBySocialNetworkComparisonSeries.getSocialSeriesUrl(filterOverrides);
                            } catch {}

                            return {
                                id: key.toUpperCase(),
                                source: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                                yValue: values[key],
                                color: socialNetworkTypeColorMap[bucket.termValue],
                                labelColor: chartColors.agprText,
                                percentage: values[key] !== 0 ? bucket.count / values[key] :  0,
                                viewMentionUrl: viewMentionUrl
                            };
                        }
                    ));
                }
            );
            return returnArray;
        };

        const mockOptions = this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SENTIMENT_BY_SOCIAL_MEDIA_COMPARISON);

        const mockMetaData = {
            titleText: this.localizationService.transform('Social.intelligence.themeAnalytics.sentiment.socialNetworkComparison.title'),
            subtitleText: this.localizationService.transform('Social.intelligence.themeAnalytics.sentiment.socialNetworkComparison.subtitle'),
            dateRange: {
                type: 'RELATIVE',
                relativeStartOffsetH: -24,
                relativeEndOffsetH: 0
            },
            labelDisplayOptions: [
                'percent'
            ],
            noContentLabel: `<span style="font-size: 0.875rem;">${this.noContentLabelText}</span>`,
            titleHeadingLevel: 4
        } as ChartMetadataModel;

        const showSentimentTrend = this.showSentimentTrendBySocialNetwork.bind(this);

        this.sentimentBySocialNetworkComparisonChart = new HorizontalStackedBarChartModel(
            [this.sentimentBySocialNetworkComparisonSeries],
            mockOptions,
            mockMetaData,
            {},
            showSentimentTrend
        );
    }

    private initializeSentimentBySocialTopicComparisonChart(): void {
        this.sentimentBySocialTopicComparisonSeries = new ReportSeriesModel();
        this.sentimentBySocialTopicComparisonSeries.mentions.facetGenerator = () => {
            return {
                facet: sentimentBySocialTopicNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };

        this.sentimentBySocialTopicComparisonSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup);

            const returnArray: DataPointModel[] = [];
            if (!neoFacet?.buckets) {
                return returnArray;
            }

            const buckets = neoFacet.buckets
                .filter(bucket => this.agentIds.find(id => id === +bucket.termValue))
                .sort((a, b) => +a.termValue - +b.termValue);
            this.hasTooManyResources = buckets.length > 8;
            buckets.forEach(
                bucket => {
                    const values = {
                        positive: 0,
                        neutral: 0,
                        negative: 0,
                        // needs to follow this convention so key.toUpperCase maps to the right thing...
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        not_Available: 0
                    };
                    (bucket.buckets[SocialSeriesNeoFacetKeysEnum.sentimentBySocialTopicFacetGroup] as any).forEach((socialTopicBucket) => {
                        if (+socialTopicBucket.termValue <= -0.3) {
                            values.negative += socialTopicBucket.count;
                        } else if (+socialTopicBucket.termValue >= 0.3) {
                            values.positive += socialTopicBucket.count;
                        } else {
                            values.neutral += socialTopicBucket.count;
                        }
                    });

                    values.not_Available = bucket.count - values.positive - values.negative - values.neutral;

                    returnArray.push(...Object.keys(values).map(
                        (key, value) => {
                            const filterOverrides = new MentionSearchCriteriaModel();
                            filterOverrides.query.agent.include = [+bucket.termValue];

                            // Necessary for socialSentimentToSearchCriteriaQuerySentimentsEnumMap
                            let k = key;
                            if (key === 'not_Available') {
                                k = 'notAvailable';
                            }
                            filterOverrides.query.sentiments = [new SentimentModel(socialSentimentToSearchCriteriaQuerySentimentsEnumMap.get(k))];

                            let viewMentionUrl: string = '';
                            try {
                                viewMentionUrl = this.sentimentBySocialTopicComparisonSeries.getSocialSeriesUrl(filterOverrides);
                            } catch {}
                            return {
                                id: key.toUpperCase(),
                                source: this.socialTopics.find(topic => topic.id === +bucket.termValue).name,
                                yValue: values[key],
                                color: chartColors.agprBlue,
                                labelColor: chartColors.agprBlue,
                                percentage: values[key] !== 0 ? bucket.count / values[key] :  0,
                                viewMentionUrl: viewMentionUrl
                            };
                        }
                    ));
                }
            );
            return returnArray;
        };

        const chartOptions = this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SENTIMENT_BY_SOCIAL_TOPIC_COMPARISON);

        const chartMetaData = {
            type: ThemeAnalyticsChartTypeEnum.SENTIMENT_BY_SOCIAL_TOPIC_COMPARISON,
            titleText: this.localizationService.transform('Social.intelligence.themeAnalytics.sentiment.socialTopicComparison.title'),
            subtitleText: this.localizationService.transform('Social.intelligence.themeAnalytics.sentiment.socialTopicComparison.subtitle'),
            dateRange: {
                type: 'RELATIVE',
                relativeStartOffsetH: -24,
                relativeEndOffsetH: 0
            },
            labelDisplayOptions: [
                'percent'
            ],
            noContentLabel: `<span style="font-size: 0.875rem;">${this.noContentLabelText}</span>`,
            titleHeadingLevel: 4
        } as ChartMetadataModel;

        const showSentimentTrend = this.showSentimentTrendBySocialTopic.bind(this);

        this.sentimentBySocialTopicComparisonChart = new HorizontalStackedBarChartModel(
            [this.sentimentBySocialTopicComparisonSeries],
            chartOptions,
            chartMetaData,
            {},
            showSentimentTrend
        );
    }

    private fillBucketWithAllMediaTypes(original: BucketResponseModel[]): BucketResponseModel[]{
        const buckets = Array.from(original);
        const allMediaTypes: MediaTypeEnum[] = this.enabledNetworkTypes
                    .filter(network => network !== NetworkTypeEnum.all && network !== NetworkTypeEnum.subReddit)
                    .map(network => networkTypeToMediaTypeMap.get(network))
                    .filter(network => !!network);

        if (buckets.length !== allMediaTypes.length){
            buckets.forEach(
                bucket => {
                    allMediaTypes.splice(allMediaTypes.indexOf(bucket.termValue as MediaTypeEnum), 1);
                }
            );
            if (allMediaTypes.length !== 0) {
                allMediaTypes.forEach(
                    type => {
                        buckets.push({
                            termValue: type,
                            count: 0,
                            buckets: { [SocialSeriesNeoFacetKeysEnum.sentimentByMediaTypeFacetGroup] : []}
                        });
                    }
                );
            }
        }
        return buckets;
    }
    private initializeSentimentBySocialMediaTrendChart(): void {
        if (!this.selectedSocialMedia) {
            this.socialNetworkTrendChartSeries = new ReportSeriesModel();
            return;
        }

        this.socialNetworkTrendChartSeries = new ReportSeriesModel();
        this.socialNetworkTrendChartSeries.key = SearchCriteriaQuerySentimentsEnum.positive;

        this.socialNetworkTrendChartSeries.mentions.filters.searchCriteria.query.includedMediaTypes = [
            new MediaTypeModel(mediaSubTypeToMediaTypeMap.get(this.selectedSocialMedia))
        ];
        this.socialNetworkTrendChartSeries.mentions.facetGenerator = () => {
            return {
                facet: sentimentOverTimeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.socialNetworkTrendChartSeries.mentions.dataMapper = (response, request, series) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.overTimeFacet);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets.map(
                bucket => {
                    const positiveSum = (bucket.buckets[SocialSeriesNeoFacetKeysEnum.sentimentOverTimeFacetGroup] as BucketResponseModel[]).reduce(
                        (previousValue, bucketValue) => {
                            return +bucketValue.termValue >= 0.3 ? previousValue + bucketValue.count : previousValue;
                        }, 0
                    );

                    const neutralSum = (bucket.buckets[SocialSeriesNeoFacetKeysEnum.sentimentOverTimeFacetGroup] as BucketResponseModel[]).reduce(
                        (previousValue, bucketValue) => {
                            return +bucketValue.termValue < 0.3 && +bucketValue.termValue > -0.3 ? previousValue + bucketValue.count : previousValue;
                        }, 0
                    );
                    const negativeSum = (bucket.buckets[SocialSeriesNeoFacetKeysEnum.sentimentOverTimeFacetGroup] as BucketResponseModel[]).reduce(
                        (previousValue, bucketValue) => {
                            return +bucketValue.termValue <= -0.3 ? previousValue + bucketValue.count : previousValue;
                        }, 0
                    );

                    // amcharts only work with local timezone, so start and end time needs to change to user preference timezone (timestamp changed)
                    const startTime = bucket.intervalValue.start;
                    const endTime = bucket.intervalValue.end;

                    const timeScale = getTimeScale(startTime, endTime);
                    const momentStart = moment(startTime).startOf(timeScale);
                    const momentEnd = moment(endTime).startOf(timeScale);
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();

                    filterOverrides.query.dateQuery.dateType = series.dateRange?.dateType;
                    // for generating the filters we should use the original start and end time
                    filterOverrides.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };
                    // Uncomment if we want to use user pref date format in the future
                    // const momentFormat = dateFormats.find(x => x.value === this.preferences.dateFormat).momentFormat;
                    const dataPoints: DataPointModel[] = [];
                    const count = this.countTotalMentions(
                        series.sentiments, positiveSum, neutralSum, negativeSum, bucket.count - positiveSum - neutralSum - negativeSum
                    );
                    series.sentiments.forEach(sentiment => {
                        const valueLabel = this.localizationService.transform('Sentiment.' + sentiment);
                        let val: number = 0;
                        switch (sentiment) {
                            case SearchCriteriaQuerySentimentsEnum.positive:
                                val = positiveSum;
                                break;
                            case SearchCriteriaQuerySentimentsEnum.neutral:
                                val = neutralSum;
                                break;
                            case SearchCriteriaQuerySentimentsEnum.negative:
                                val = negativeSum;
                                break;
                            default:
                                val = bucket.count - positiveSum - negativeSum - neutralSum;
                        }
                        filterOverrides.query.sentiments = [new SentimentModel(sentiment)];
                        const dataPoint = {
                            mappingKeys: {
                                [ReportsDataMappingKeysEnum.DATE]: momentStart.valueOf(),
                                [ReportsDataMappingKeysEnum.SENTIMENT]: sentiment,
                            },
                            value: val,
                            percent: parseFloat((safeDivide(val, bucket.count) * 100).toFixed(1)),
                            valueLabel: valueLabel,
                            viewUrl: series.getSocialSeriesUrl(filterOverrides),
                            mentionCount: count,
                            startTimeExport: momentStart.format('YYYY/MM/DD HH:mm'),
                            endTimeExport: momentEnd.format('YYYY/MM/DD HH:mm'),
                            rawStartDate: bucket.intervalValue.start,
                            rawEndDate: bucket.intervalValue.end,
                            timeScale: timeScale
                        };
                        dataPoints.push(dataPoint);
                    });

                    return dataPoints;
                }
            ).flat();
        };

        const sentimentTypes = [
            SearchCriteriaQuerySentimentsEnum.positive,
            SearchCriteriaQuerySentimentsEnum.neutral,
            SearchCriteriaQuerySentimentsEnum.notAvailable,
            SearchCriteriaQuerySentimentsEnum.negative
        ];
        this.socialNetworkTrendChartSeries.sentiments = sentimentTypes;
        const sentimentColors = {};
        const sentimentColTypes = sentimentTypes;
        sentimentColTypes.forEach(color => {
            sentimentColors[color] = this.sentimentsToChartColorsMap.get(color) || stackedBarColorsConfig[color];
        });
        this.socialNetworkTrendChartSeries.styles.colorMap = sentimentColors;
        this.socialNetworkTrendSeries = [this.socialNetworkTrendChartSeries];
        this.socialNetworkTrendChart = this.buildSocialSentimentTrendChart(
            this.socialNetworkTrendChartSeries,
            this.selectedSentimentTrendNetworkChartTabIndex,
            'stackedTrendChartSocialNetwork'
        );
    }

    countTotalMentions(sentiments, positiveSum, neutralSum, negativeSum, notAvailableSum): number {
        let count = 0;
        sentiments.forEach(sentiment => {
            if (SearchCriteriaQuerySentimentsEnum.positive === sentiment) {
                count += positiveSum;
            } else if (SearchCriteriaQuerySentimentsEnum.neutral === sentiment) {
                count += neutralSum;
            } else if (SearchCriteriaQuerySentimentsEnum.negative === sentiment) {
                count += negativeSum;
            } else {
                count += notAvailableSum;
            }
        });
        return count;
    }

    private initializeSentimentBySocialTopicTrendChart(): void {
        if (!this.selectedSocialTopicName) {
            this.socialTopicTrendChartSeries = new ReportSeriesModel();
            return;
        }

        this.socialTopicTrendChartSeries = new ReportSeriesModel();
        this.socialTopicTrendChartSeries.key = SearchCriteriaQuerySentimentsEnum.positive;
        this.socialTopicTrendChartSeries.mentions.filters.searchCriteria.query.agent.include = [
            this.socialTopics.find(socialTopic => socialTopic.name === this.selectedSocialTopicName).id
        ];
        this.socialTopicTrendChartSeries.mentions.facetGenerator = () => {
            return{
                facet: sentimentOverTimeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.socialTopicTrendChartSeries.mentions.dataMapper = (response, request, series) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.overTimeFacet);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets.map(
                bucket => {
                    const positiveSum = (bucket.buckets[SocialSeriesNeoFacetKeysEnum.sentimentOverTimeFacetGroup] as BucketResponseModel[]).reduce(
                        (previousValue, bucketValue) => {
                            return +bucketValue.termValue >= 0.3 ? previousValue + bucketValue.count : previousValue;
                        }, 0
                    );

                    const neutralSum = (bucket.buckets[SocialSeriesNeoFacetKeysEnum.sentimentOverTimeFacetGroup] as BucketResponseModel[]).reduce(
                        (previousValue, bucketValue) => {
                            return +bucketValue.termValue < 0.3 && +bucketValue.termValue > -0.3 ? previousValue + bucketValue.count : previousValue;
                        }, 0
                    );
                    const negativeSum = (bucket.buckets[SocialSeriesNeoFacetKeysEnum.sentimentOverTimeFacetGroup] as BucketResponseModel[]).reduce(
                        (previousValue, bucketValue) => {
                            return +bucketValue.termValue <= -0.3 ? previousValue + bucketValue.count : previousValue;
                        }, 0
                    );
                    // amcharts only work with local timezone, so start and end time needs to change to user preference timezone (timestamp changed)
                    const startTime = bucket.intervalValue.start;
                    const endTime = bucket.intervalValue.end;

                    const timeScale = getTimeScale(startTime, endTime);
                    const momentStart = moment(startTime).startOf(timeScale);
                    const momentEnd = moment(endTime).startOf(timeScale);
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();

                    filterOverrides.query.dateQuery.dateType = series.dateRange?.dateType;
                    // for generating the filters we should use the original start and end time
                    filterOverrides.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };
                    // Uncomment if we want to use user pref date format in the future
                    // const momentFormat = dateFormats.find(x => x.value === this.preferences.dateFormat).momentFormat;
                    const dataPoints: DataPointModel[] = [];
                    const count = this.countTotalMentions(
                        series.sentiments, positiveSum, neutralSum, negativeSum, bucket.count - positiveSum - neutralSum - negativeSum
                    );
                    series.sentiments.forEach(sentiment => {
                        const valueLabel = this.localizationService.transform('Sentiment.' + sentiment);
                        let val: number = 0;
                        switch (sentiment) {
                            case SearchCriteriaQuerySentimentsEnum.positive:
                                val = positiveSum;
                                break;
                            case SearchCriteriaQuerySentimentsEnum.neutral:
                                val = neutralSum;
                                break;
                            case SearchCriteriaQuerySentimentsEnum.negative:
                                val = negativeSum;
                                break;
                            default:
                                val = bucket.count - positiveSum - negativeSum - neutralSum;
                        }
                        filterOverrides.query.sentiments = [new SentimentModel(sentiment)];
                        const dataPoint = {
                            mappingKeys: {
                                [ReportsDataMappingKeysEnum.DATE]: momentStart.valueOf(),
                                [ReportsDataMappingKeysEnum.SENTIMENT]: sentiment,
                            },
                            value: val,
                            percent: parseFloat((safeDivide(val, bucket.count) * 100).toFixed(1)),
                            valueLabel: valueLabel,
                            viewUrl: series.getSocialSeriesUrl(filterOverrides),
                            mentionCount: count,
                            startTimeExport: momentStart.format('YYYY/MM/DD HH:mm'),
                            endTimeExport: momentEnd.format('YYYY/MM/DD HH:mm'),
                            rawStartDate: bucket.intervalValue.start,
                            rawEndDate: bucket.intervalValue.end,
                            timeScale: timeScale
                        };
                        dataPoints.push(dataPoint);
                    });

                    return dataPoints;
                }
            ).flat();
        };

        const sentimentTypes = [
            SearchCriteriaQuerySentimentsEnum.positive,
            SearchCriteriaQuerySentimentsEnum.neutral,
            SearchCriteriaQuerySentimentsEnum.notAvailable,
            SearchCriteriaQuerySentimentsEnum.negative
        ];
        this.socialTopicTrendChartSeries.sentiments = sentimentTypes;
        const sentimentColors = {};
        const sentimentColTypes = sentimentTypes;
        sentimentColTypes.forEach(color => {
            sentimentColors[color] = this.sentimentsToChartColorsMap.get(color) || stackedBarColorsConfig[color];
        });
        this.socialTopicTrendChartSeries.styles.colorMap = sentimentColors;
        this.socialTopicTrendSeries = [this.socialTopicTrendChartSeries];
        this.socialTopicTrendChart = this.buildSocialSentimentTrendChart(
            this.socialTopicTrendChartSeries,
            this.selectedSentimentTrendTopicChartTabIndex,
            'stackedTrendChartSocialTopic'
        );
    }

    showSentimentTrendBySocialNetwork(event: any): void {
        this.showSentimentBySocialNetworkTrendChart = true;
        this.selectedSocialMedia = event.toUpperCase() as MediaSubscriptionMediaTypeEnum;

        // @hack: uses label here instead of value or id
        if ((this.selectedSocialMedia as string) === 'X') {
            this.selectedSocialMedia = MediaSubscriptionMediaTypeEnum.twitter;
        }

        this.socialSeriesService.deregisterSeries([
            // sentiment trend Chart
            this.socialNetworkTrendChartSeries,
        ]);

        this.initializeSentimentBySocialMediaTrendChart();

        this.idToChartModelMap.set('stackedTrendChartSocialNetwork', this.socialNetworkTrendChart);

        this.socialSeriesService.fulfillSeries([
            // sentiment trend Chart
            this.socialNetworkTrendChartSeries,
        ]);

    }

    hideSentimentTrendBySocialNetwork(): void {
        this.showSentimentBySocialNetworkTrendChart = false;
        this.selectedSentimentTrendNetworkChartTabIndex = 0;
    }

    showSentimentTrendBySocialTopic(event: any): void {
        this.showSentimentBySocialTopicTrendChart = true;
        this.selectedSocialTopicName = event;
        this.socialSeriesService.deregisterSeries([
            this.socialTopicTrendChartSeries,
        ]);

        this.initializeSentimentBySocialTopicTrendChart();

        this.idToChartModelMap.set('stackedTrendChartSocialTopic', this.socialTopicTrendChart);

        this.socialSeriesService.fulfillSeries([
            this.socialTopicTrendChartSeries,
        ]);
    }
    hideSentimentTrendBySocialTopic(): void {
        this.showSentimentBySocialTopicTrendChart = false;
        this.selectedSentimentTrendTopicChartTabIndex = 0;
    }

    public initializeTopPosts(): void {
        this.positiveTopPostsSeries = new ReportSeriesModel();
        this.positiveTopPostsSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        this.positiveTopPostsSortField = SearchCriteriaSortFieldEnum.engagement_total;
        this.positiveTopPostsSeries.mentions.mentionRequestGenerator = () => {
            return {
                sortOrder: SortOrderEnum.descending,
                sortField: SearchCriteriaSortFieldEnum.engagement_total,
                pageSize: 5
            };
        };
        this.positiveTopPostsSeries.mentions.mentionDataMapper = (response) => {
            return response.mentions.map(apiMention => new Mention(apiMention));
        };
        this.positiveTopPostsSeries.mentions.filters = new MentionSearchModel();
        this.positiveTopPostsSeries.mentions.filters.searchCriteria.query.sentiments = [ new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive) ];

        this.negativeTopPostsSeries = new ReportSeriesModel();
        this.negativeTopPostsSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        this.negativeTopPostsSortField = SearchCriteriaSortFieldEnum.engagement_total;
        this.negativeTopPostsSeries.mentions.mentionRequestGenerator = () => {
            return {
                sortOrder: SortOrderEnum.descending,
                sortField: SearchCriteriaSortFieldEnum.engagement_total,
                pageSize: 5
            };
        };
        this.negativeTopPostsSeries.mentions.mentionDataMapper = (response) => {
            return response.mentions.map(apiMention => new Mention(apiMention));
        };
        this.negativeTopPostsSeries.mentions.filters = new MentionSearchModel();
        this.negativeTopPostsSeries.mentions.filters.searchCriteria.query.sentiments = [ new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative) ];
    }

    public initializeTopTerms(): void {
        const facetGenerator = () => {
            return {
                facet: [
                    // Initial Facet hashtags
                    MentionSearchFacetTypesEnum.hashtags,
                    MentionSearchFacetTypesEnum.sentiment
                ],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };

        this.positiveTopTermsSeries = new ReportSeriesModel();
        this.positiveTopTermsSeries.dataProvider = new BehaviorSubject<DataPointModel[]>(null);
        this.positiveTopTermsSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        this.positiveTopTermsSortField = SearchCriteriaSortFieldEnum.audience_reach;
        this.positiveTopTermsSeries.mentions.facetGenerator = facetGenerator;
        this.positiveTopTermsSeries.mentions.filters = new MentionSearchModel();
        this.positiveTopTermsSeries.mentions.filters.searchCriteria.query.sentiments = [ new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive) ];
        this.positiveTopTermsSeries.mentions.dataMapper = TopTermsDataMappers.hashtagDataMapper(this.positiveTopTermsSeries);

        this.negativeTopTermsSeries = new ReportSeriesModel();
        this.negativeTopTermsSeries.dataProvider = new BehaviorSubject<DataPointModel[]>(null);
        this.negativeTopTermsSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        this.negativeTopTermsSortField = SearchCriteriaSortFieldEnum.audience_reach;
        this.negativeTopTermsSeries.mentions.facetGenerator = facetGenerator;
        this.negativeTopTermsSeries.mentions.filters = new MentionSearchModel();
        this.negativeTopTermsSeries.mentions.filters.searchCriteria.query.sentiments = [ new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative) ];
        this.negativeTopTermsSeries.mentions.dataMapper =  TopTermsDataMappers.hashtagDataMapper(this.negativeTopTermsSeries);
    }

    prepareChartsForDrillDownPage(chart: ChartModel | Am5Chart): ChartModel | Am5Chart {
        if (chart instanceof HorizontalStackedBarChartModel) {
            chart.options.hoverEffect = false;
        }

        return chart;
    }
    public exportChart(event: ChartExportEventModel, elementRef: any, chartType: ThemeAnalyticsChartTypeEnum) {
        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        const exportModel = this.prepareExportData(chartType);
        if (exportType === 'csv' || exportType === 'json') {
            this.chartExportService.exportData(exportType, exportModel);
        } else {
            const exportMenuElement = elementRef.querySelector('.agpr-themeSentimentAnalytics-exportMenu');
            exportMenuElement.style.display = 'none';
            this.chartExportService.exportImage(exportType, elementRef, exportModel.title).then(() => {
                exportMenuElement.style.display = 'block';
            });
        }
    }

    private prepareExportData(chartType: ThemeAnalyticsChartTypeEnum): SocialChartExportModel {
        switch (chartType) {
            case ThemeAnalyticsChartTypeEnum.SENTIMENT_SUMMARY:
                return {
                    title: this.sentimentSummaryChart.metadata.titleText,
                    chartModel: this.sentimentSummaryChart
                };

            case ThemeAnalyticsChartTypeEnum.SENTIMENT_BY_SOCIAL_MEDIA_COMPARISON:
                return {
                    title: this.sentimentBySocialNetworkComparisonChart.metadata.titleText,
                    chartModel: this.sentimentBySocialNetworkComparisonChart
                };

            case ThemeAnalyticsChartTypeEnum.SENTIMENT_BY_SOCIAL_TOPIC_COMPARISON:
                return {
                    title: this.sentimentBySocialTopicComparisonChart.metadata.titleText,
                    chartModel: this.sentimentBySocialTopicComparisonChart
                };

            case ThemeAnalyticsChartTypeEnum.SOCIAL_MEDIA_SENTIMENT_TREND:
                return {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.sentimentTrend'),
                    chartModel: this.socialNetworkTrendChart
                };

            case ThemeAnalyticsChartTypeEnum.SOCIAL_TOPIC_SENTIMENT_TREND:
                return {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.sentimentTrend'),
                    chartModel: this.socialTopicTrendChart
                };

            default:
                console.error('cannot find chart type');
                break;
        }
    }
}

const sentimentNeoFacetRequest: NeoFacetRequestApiModel = {
    termField: NeoFacetTermFieldApiEnum.MEDIA_TYPE,
    key: SocialSeriesNeoFacetKeysEnum.sentimentFacetGroup,
    plainBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.sentimentByMediaTypeFacetGroup,
            field: NeoFacetTermFieldApiEnum.SENTIMENT
        }
    ]
};

const sentimentBySocialTopicNeoFacetRequest: NeoFacetRequestApiModel = {
    termField: NeoFacetTermFieldApiEnum.MATCHED_AGENTS,
    key: SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup,
    plainBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.sentimentBySocialTopicFacetGroup,
            field: NeoFacetTermFieldApiEnum.SENTIMENT
        }
    ]
};


const sentimentOverTimeNeoFacetRequest: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: SocialSeriesNeoFacetKeysEnum.overTimeFacet,
    plainBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.sentimentOverTimeFacetGroup,
            field: NeoFacetTermFieldApiEnum.SENTIMENT
        }
    ]
};
